<template>
  <v-card flat>
    <v-card-text class="d-flex align-center flex-wrap">
      <user-avatar :user="user" :size="100" font-size="36px" class="mr-6 my-2" data-test-profile-avatar />
      <div class="d-flex flex-column my-2">
        <div class="d-flex flex-wrap">
          <v-btn
            color="primary"
            class="mr-3 mb-2"
            :loading="isUploading"
            :disabled="isRemoving || isUploading"
            @click="openFilePicker"
            data-test-profile-avatar-upload
          >
            <v-icon left>mdi-cloud-upload-outline</v-icon>
            {{ $t("components.profile.ProfileAvatar.upload") }}
          </v-btn>
          <v-btn
            v-if="hasAvatar"
            color="error"
            outlined
            class="mb-2"
            :loading="isRemoving"
            :disabled="isUploading"
            @click="remove"
            data-test-profile-avatar-remove
          >
            {{ $t("components.profile.ProfileAvatar.remove") }}
          </v-btn>
          <input
            ref="fileInput"
            type="file"
            :accept="accept"
            hidden
            @change="onFileSelected"
            data-test-profile-avatar-input
          />
        </div>
        <span class="text-body-2 text--secondary">
          {{ $t("components.profile.ProfileAvatar.hint", { size: maxFileSizeMb }) }}
        </span>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import UserAvatar from "@/components/utility/UserAvatar.vue";
import { IUserAvatarSource } from "@/lib/utility/user-avatar";
import { IUser } from "@/models/user.entity";
import { UserModule } from "@/store/modules/me-user.store";
import { Component, Ref, Vue } from "vue-property-decorator";

/**
 * Upload and removal of the own profile picture, shown at the top of the profile tab.
 * The picked file is previewed right away while the upload runs; once the backend answered the
 * stored variants replace the preview and every avatar in the app updates through the user store.
 */
@Component({
  components: { UserAvatar }
})
export default class ProfileAvatar extends Vue {
  readonly maxFileSizeMb = 2;

  readonly accept = "image/jpeg,image/png,.jpg,.jpeg,.png";

  @Ref("fileInput")
  fileInput!: HTMLInputElement;

  isUploading = false;

  isRemoving = false;

  previewUrl = "";

  get storedUser(): IUser {
    return UserModule.user;
  }

  get user(): IUserAvatarSource {
    if (!this.previewUrl) {
      return this.storedUser;
    }

    return {
      firstName: this.storedUser?.firstName,
      lastName: this.storedUser?.lastName,
      avatar: { thumbnail: this.previewUrl, small: this.previewUrl, medium: this.previewUrl, large: this.previewUrl }
    };
  }

  get hasAvatar(): boolean {
    return !!this.storedUser?.avatar;
  }

  openFilePicker() {
    this.fileInput.value = "";
    this.fileInput.click();
  }

  async onFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file || this.isUploading) {
      return;
    }

    if (file.size > this.maxFileSizeMb * 1024 * 1024) {
      this.$toast.error(this.$t("components.profile.ProfileAvatar.tooLarge", { size: this.maxFileSizeMb }));
      return;
    }

    this.previewUrl = URL.createObjectURL(file);
    this.isUploading = true;
    try {
      await UserModule.uploadAvatar(file);
      this.$toast.success(this.$t("components.profile.ProfileAvatar.uploaded"));
    } catch (error) {
      this.$log.error(error);
      this.$toast.error(this.$t("components.profile.ProfileAvatar.error"));
    } finally {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = "";
      this.isUploading = false;
    }
  }

  async remove() {
    this.isRemoving = true;
    try {
      await UserModule.removeAvatar();
      this.$toast.success(this.$t("components.profile.ProfileAvatar.removed"));
    } catch (error) {
      this.$log.error(error);
      this.$toast.error(this.$t("components.profile.ProfileAvatar.error"));
    } finally {
      this.isRemoving = false;
    }
  }
}
</script>
